import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Chip,
    Divider,
    CircularProgress
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { BRAND, BRAND_DARK, BRAND_LIGHT_BG } from '../constants/salesPromotionsTheme';

const OrderPreview = ({
    open,
    onClose,
    onConfirm,
    orderData,
    customerDescription,
    translation,
    isLoading = false // loading-state prop
}) => {
    if (!orderData) return null;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString + 'T00:00:00');
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getDeliveryAddress = () => {
        if (orderData.site) {
            return orderData.site;
        }

        const { address } = orderData;
        if (!address) return 'לא צוינה כתובת';

        const parts = [
            address.street,
            address.hoNumber,
            address.city
        ].filter(Boolean);

        return parts.join(', ') || 'לא צוינה כתובת';
    };

    const getTotalQuantity = () => {
        return orderData.selectedProducts.reduce((total, product) => total + (product.quantity || 0), 0);
    };

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose} // prevent closing while loading
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3, maxHeight: '90vh' }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    backgroundColor: '#fafbfc',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    py: 2
                }}
            >
                <Box
                    sx={{
                        p: 1,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(25,118,210,0.14) 0%, rgba(25,118,210,0.05) 100%)',
                        display: 'flex',
                    }}
                >
                    <AssignmentTurnedInIcon sx={{ color: BRAND, fontSize: 24 }} />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                    אישור הזמנה - תצוגה מקדימה
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ padding: 2, opacity: isLoading ? 0.7 : 1 }}>
                <Grid container spacing={2}>
                    {/* פרטי לקוח ואספקה */}
                    <Grid item xs={12} md={8}>
                        <Card elevation={0} sx={{ height: '100%', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3 }}>
                            <CardContent sx={{ pb: 2 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                                    פרטי ההזמנה
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            לקוח מזמין:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600">
                                            {customerDescription?.orderingCustomer?.description}
                                        </Typography>
                                    </Box>

                                    {orderData.selectedRecivingCustomer && (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                לקוח מקבל:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="600">
                                                {customerDescription?.receivingCustomer?.description}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 1 }} />

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            כתובת אספקה:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600">
                                            {getDeliveryAddress()}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            תאריך אספקה:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="600" sx={{ color: '#1976d2' }}>
                                            {formatDate(orderData.deliveryDate)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* סיכום מהיר */}
                    <Grid item xs={12} md={4}>
                        <Card elevation={0} sx={{ height: '100%', backgroundColor: '#fafbfc', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3 }}>
                            <CardContent sx={{ textAlign: 'center', pb: 2 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                                    סיכום ההזמנה
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box>
                                        <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                            {orderData.selectedProducts.length}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            סוגי מוצרים
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                            {getTotalQuantity()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            יחידות סה"כ
                                        </Typography>
                                    </Box>

                                    {orderData.selectedFiles && orderData.selectedFiles.length > 0 && (
                                        <Box>
                                            <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                                {orderData.selectedFiles.length}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                קבצים מצורפים
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* רשימת מוצרים */}
                    <Grid item xs={12}>
                        <Card elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3 }}>
                            <CardContent sx={{ pb: 1 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#1976d2' }}>
                                    מוצרים בהזמנה
                                </Typography>

                                <Box sx={{
                                    maxHeight: orderData.selectedProducts.length > 3 ? 200 : 'auto',
                                    overflow: 'auto',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    backgroundColor: '#fafafa'
                                }}>
                                    <List dense sx={{ py: 0 }}>
                                        {orderData.selectedProducts.map((product, index) => (
                                            <ListItem
                                                key={index}
                                                sx={{
                                                    borderBottom: index < orderData.selectedProducts.length - 1 ? '1px solid #e9e9e9' : 'none',
                                                    py: 1
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" fontWeight="500">
                                                            {product.PARTDES}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="text.secondary">
                                                            {product.PARTNAME}
                                                        </Typography>
                                                    }
                                                />
                                                <Chip
                                                    label={product.quantity}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#1976d2',
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        minWidth: '40px'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* מצב טעינה - overlay */}
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 1000,
                            borderRadius: 2
                        }}
                    >
                        <CircularProgress
                            size={60}
                            sx={{ color: '#1976d2', mb: 2 }}
                        />
                        <Typography
                            variant="h6"
                            sx={{ color: '#1976d2', fontWeight: 'bold', textAlign: 'center' }}
                        >
                            שולח הזמנה...
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ textAlign: 'center', mt: 1 }}
                        >
                            אנא המתן, הפעולה עלולה להימשך כמה שניות
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: 2, backgroundColor: '#fafbfc', gap: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    size="large"
                    disabled={isLoading}
                    sx={{
                        color: isLoading ? 'rgba(0, 0, 0, 0.26)' : BRAND,
                        borderColor: isLoading ? 'rgba(0, 0, 0, 0.12)' : BRAND,
                        '&:hover': !isLoading ? {
                            backgroundColor: BRAND_LIGHT_BG,
                            borderColor: BRAND_DARK
                        } : {},
                        minWidth: 140,
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 600
                    }}
                >
                    חזרה לעריכה
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                        backgroundColor: isLoading ? 'rgba(0, 0, 0, 0.12)' : BRAND,
                        color: isLoading ? 'rgba(0, 0, 0, 0.26)' : '#fff',
                        fontWeight: 600,
                        '&:hover': !isLoading ? {
                            backgroundColor: BRAND_DARK
                        } : {},
                        minWidth: 140,
                        borderRadius: 2.5,
                        textTransform: 'none',
                        position: 'relative'
                    }}
                >
                    {isLoading && (
                        <CircularProgress
                            size={20}
                            sx={{
                                color: '#1976d2',
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                marginLeft: '-10px',
                                marginTop: '-10px'
                            }}
                        />
                    )}
                    <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
                        אשר ושלח הזמנה
                    </span>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderPreview;